import type { Tables } from "@/types/supabase-helpers";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import type { BuildingDataAttributeRow } from "@/stores/admin/models";
import type { BuildingDataAttributeUpdate } from "@/stores/admin/models";
import type { AttributeUpdate } from "./types";

export const useAttributeUpdates = (attributesList: BuildingDataAttributeRow[]) => {
  const [attributeUpdates, setAttributeUpdates] = useState<Record<string, AttributeUpdate>>({});

  useEffect(() => {
    const updates: Record<string, AttributeUpdate> = {};
    attributesList.forEach((attr) => {
      updates[attr.attribute_nm] = {
        group_id: attr.group_id,
        group_nm: attr.group_nm,
        name_en: attr.name_en,
        name_et: attr.name_et,
        definition_en: attr.definition_en,
        default_unit: attr.default_unit,
        attribute_type_en: attr.attribute_type_en,
        value_type: attr.value_type,
        display_on_widget: attr.display_on_widget ?? false,
        sort_order: attr.sort_order ?? null,
      };
    });
    setAttributeUpdates(updates);
  }, [attributesList]);

  const handleFieldChange = useCallback(
    (
      attributeName: string,
      field: keyof AttributeUpdate,
      value: string | number | boolean | null
    ) => {
      setAttributeUpdates((prev) => ({
        ...prev,
        [attributeName]: {
          ...prev[attributeName],
          [field]: value,
        },
      }));
    },
    []
  );

  return { attributeUpdates, setAttributeUpdates, handleFieldChange };
};

export const useAttributeActions = () => {
  const { buildingDataAttributesStore } = useStores();
  const [savingAttribute, setSavingAttribute] = useState<string | null>(null);
  const [deletingAttribute, setDeletingAttribute] = useState<string | null>(null);

  const handleSave = useCallback(
    async (attributeName: string, update: AttributeUpdate) => {
      if (!update) return;

      setSavingAttribute(attributeName);
      try {
        await buildingDataAttributesStore.updateAttribute(
          attributeName,
          update as BuildingDataAttributeUpdate
        );
        toast.success("Attribute updated successfully");
      } catch (error) {
        toast.error("Failed to update attribute");
      } finally {
        setSavingAttribute(null);
      }
    },
    [buildingDataAttributesStore]
  );

  const handleDelete = useCallback(
    async (attributeName: string) => {
      if (!confirm(`Are you sure you want to delete attribute "${attributeName}"?`)) {
        return;
      }

      setDeletingAttribute(attributeName);
      try {
        await buildingDataAttributesStore.deleteAttribute(attributeName);
        toast.success("Attribute deleted successfully");
      } catch (error) {
        toast.error("Failed to delete attribute");
      } finally {
        setDeletingAttribute(null);
      }
    },
    [buildingDataAttributesStore]
  );

  return { savingAttribute, deletingAttribute, handleSave, handleDelete };
};

export const useAttributeRelationships = () => {
  const { buildingDataAttributesStore, documentCategoriesStore } = useStores();
  const [attributeCategories, setAttributeCategories] = useState<Record<string, number[]>>({});

  const handleCategoriesChange = useCallback(
    async (attributeName: string, categoryIds: (number | string)[]) => {
      try {
        await buildingDataAttributesStore.setAttributeCategories(
          attributeName,
          categoryIds as number[]
        );
        setAttributeCategories((prev) => ({
          ...prev,
          [attributeName]: categoryIds as number[],
        }));

        await documentCategoriesStore.loadLeafCategories();

        toast.success("Categories updated");
      } catch (error) {
        toast.error("Failed to update categories");
      }
    },
    [buildingDataAttributesStore, documentCategoriesStore]
  );

  return {
    attributeCategories,
    setAttributeCategories,
    handleCategoriesChange,
  };
};

export const useDocumentCategories = () => {
  const { documentCategoriesStore } = useStores();
  const [documentCategories, setDocumentCategories] = useState<Tables<"document_categories">[]>([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        await documentCategoriesStore.loadLeafCategories();
        setDocumentCategories(documentCategoriesStore.categoriesList);
      } catch (error) {
        toast.error("Failed to load document categories");
      }
    };
    loadCategories();
  }, [documentCategoriesStore]);

  return documentCategories;
};

export const useAttributeFilter = (searchQuery: string) => {
  return useCallback(
    (attributes: BuildingDataAttributeRow[]) => {
      if (!searchQuery.trim()) return attributes;

      const query = searchQuery.toLowerCase();
      return attributes.filter((attr) => {
        return (
          attr.attribute_nm.toLowerCase().includes(query) ||
          attr.group_nm.toLowerCase().includes(query) ||
          attr.name_en?.toLowerCase().includes(query) ||
          attr.name_et?.toLowerCase().includes(query) ||
          attr.attribute_type_en?.toLowerCase().includes(query)
        );
      });
    },
    [searchQuery]
  );
};
